// Normalizzazione e validazione delle opzioni. Specchio di
// `CloseYourItOptions`/`Configuration` (dart/gem): i tre campi obbligatori
// mancanti disabilitano l'SDK (no-op) invece di sollevare.

import { normalizeLogLevel } from './logLevel'
import type { BeforeSend, CloseYourItOptions, LogLevel } from './types'

/// Soglia di default (ms) per una richiesta HTTP esterna lenta (breadcrumb
/// `slow_external_http` + verdetto performance). Parità ruby/dart.
export const DEFAULT_SLOW_EXTERNAL_THRESHOLD_MS = 1000

export interface ResolvedOptions {
  endpointUrl: string
  token: string
  projectId: string
  environment: string
  release: string | undefined
  sampleRate: number
  beforeSend: BeforeSend | undefined
  debug: boolean
  autoInstall: boolean
  breadcrumbs: boolean
  autoBreadcrumbs: {
    click: boolean
    navigation: boolean
    console: boolean
    http: boolean
  }
  maxBreadcrumbs: number
  sendPii: boolean
  maxQueue: number
  excludedExceptions: string[]
  captureRequest: boolean
  trackPageviews: boolean
  logsEnabled: boolean
  logsSampleRate: number
  logsBatchSize: number
  logsFlushIntervalMs: number
  logsMinLevel: LogLevel
  slowMethodThresholdMs: number
  captureMethodArguments: boolean
  detectPerformanceIssues: boolean
  captureExternalHttp: boolean
  slowExternalThresholdMs: number
  repeatedHttpThreshold: number
  repeatedHttpWindowMs: number
  detectJank: boolean
  jankThresholdMs: number
  enabled: boolean
}

export function resolveOptions(options: CloseYourItOptions): ResolvedOptions {
  return {
    endpointUrl: (options.endpointUrl ?? '').trim().replace(/\/+$/, ''),
    token: (options.token ?? '').trim(),
    projectId: (options.projectId ?? '').trim(),
    environment: options.environment ?? 'production',
    release: options.release,
    sampleRate: clampRate(options.sampleRate),
    beforeSend: options.beforeSend,
    debug: options.debug ?? false,
    autoInstall: options.autoInstall ?? true,
    breadcrumbs: options.breadcrumbs ?? true,
    autoBreadcrumbs: {
      click: options.autoBreadcrumbs?.click ?? true,
      navigation: options.autoBreadcrumbs?.navigation ?? true,
      console: options.autoBreadcrumbs?.console ?? true,
      http: options.autoBreadcrumbs?.http ?? true,
    },
    maxBreadcrumbs: options.maxBreadcrumbs ?? 50,
    sendPii: options.sendPii ?? false,
    maxQueue: positiveOr(options.maxQueue, 30),
    excludedExceptions: options.excludedExceptions ?? [],
    captureRequest: options.captureRequest ?? true,
    trackPageviews: options.trackPageviews ?? false,
    logsEnabled: options.logsEnabled ?? true,
    logsSampleRate: clampRate(options.logsSampleRate),
    logsBatchSize: positiveOr(options.logsBatchSize, 50),
    logsFlushIntervalMs: options.logsFlushIntervalMs ?? 5000,
    logsMinLevel: normalizeLogLevel(options.logsMinLevel ?? 'info'),
    slowMethodThresholdMs: positiveOr(options.slowMethodThresholdMs, 200),
    captureMethodArguments: options.captureMethodArguments ?? false,
    detectPerformanceIssues: options.detectPerformanceIssues ?? false,
    captureExternalHttp: options.captureExternalHttp ?? true,
    slowExternalThresholdMs: clampThreshold(options.slowExternalThresholdMs),
    repeatedHttpThreshold: positiveOr(options.repeatedHttpThreshold, 5),
    repeatedHttpWindowMs: positiveOr(options.repeatedHttpWindowMs, 5000),
    detectJank: options.detectJank ?? true,
    jankThresholdMs: positiveOr(options.jankThresholdMs, 100),
    enabled: options.enabled ?? true,
  }
}

function clampRate(rate: number | undefined): number {
  if (rate === undefined || Number.isNaN(rate)) return 1
  if (rate < 0) return 0
  if (rate > 1) return 1
  return rate
}

// Valore > 0, altrimenti default (undefined/NaN/zero/negativi inclusi).
function positiveOr(value: number | undefined, fallback: number): number {
  if (value === undefined || Number.isNaN(value) || value <= 0) return fallback
  return value
}

// Soglia in ms: `0` è valido (disattiva la rilevazione); undefined/NaN/negativi
// ricadono sul default.
function clampThreshold(value: number | undefined): number {
  if (value === undefined || Number.isNaN(value) || value < 0) {
    return DEFAULT_SLOW_EXTERNAL_THRESHOLD_MS
  }
  return value
}

/// Motivo per cui l'SDK è disabilitato, o `null` se attivo.
export function disabledReason(options: ResolvedOptions): string | null {
  if (!options.enabled) return 'disabilitato da configurazione (enabled: false)'
  if (!options.endpointUrl) return 'endpointUrl mancante'
  if (!options.token) return 'token mancante'
  if (!options.projectId) return 'projectId mancante'
  if (
    options.environment === 'production' &&
    options.endpointUrl.startsWith('http://')
  ) {
    return 'endpoint http:// non ammesso in produzione'
  }
  return null
}

export function isEnabled(options: ResolvedOptions): boolean {
  return disabledReason(options) === null
}

/// URI di ingest per gli eventi errore/messaggio.
export function eventsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/events`
}

/// URI di ingest per i log (batch).
export function logsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/logs`
}

/// URI di ingest per le metriche (slow_method, performance_issue).
export function metricsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/metrics`
}

/// URI di ingest per le pageview (web analytics).
export function pageviewsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/pageviews`
}

/// Host dell'endpoint (per escludere la telemetria dai breadcrumb HTTP).
export function endpointHost(options: ResolvedOptions): string | undefined {
  try {
    return new URL(options.endpointUrl).host
  } catch {
    return undefined
  }
}
